/**
 * CATALOGO DE FORMAS.
 *
 * Un escudo y una banderola resuelven la estructura de la mayoria de los
 * disenos de pena y de equipo sin que el cliente tenga que subir nada. Con solo
 * rectangulo, circulo y linea, quien no trae logotipo se queda sin poder
 * empezar.
 *
 * Cada forma es una funcion de (ancho, alto) a un trazado SVG **en
 * milimetros**, como manda P2. Vive en `design-core` porque el compositor de
 * produccion dibuja el mismo trazado que el editor: si el escudo del lienzo y
 * el del arte final se generaran por separado, un dia dejarian de ser el mismo.
 */

export type NombreForma =
  | 'rectangulo'
  | 'rectangulo-redondeado'
  | 'circulo'
  | 'linea'
  | 'triangulo'
  | 'estrella'
  | 'poligono'
  | 'corazon'
  | 'flecha'
  | 'escudo'
  | 'banderola';

export interface OpcionesForma {
  /** Puntas de la estrella o lados del poligono. */
  puntas?: number;
  /** Radio de las esquinas redondeadas, en mm. */
  radioMm?: number;
  /** Cuanto se hunde la estrella hacia el centro, de 0 a 1. */
  hundido?: number;
}

const n = (v: number): string => Number(v.toFixed(3)).toString();

/**
 * Poligono regular de `lados` inscrito en el rectangulo, con la punta arriba.
 *
 * Empieza en -90 grados justamente para eso: un pentagono con un lado plano
 * arriba se lee como un boton, no como un pentagono.
 */
function poligono(ancho: number, alto: number, lados: number): string {
  const cx = ancho / 2;
  const cy = alto / 2;
  const puntos: string[] = [];
  for (let i = 0; i < lados; i += 1) {
    const angulo = (i / lados) * Math.PI * 2 - Math.PI / 2;
    puntos.push(`${n(cx + cx * Math.cos(angulo))} ${n(cy + cy * Math.sin(angulo))}`);
  }
  return `M ${puntos.join(' L ')} Z`;
}

function estrella(ancho: number, alto: number, puntas: number, hundido: number): string {
  const cx = ancho / 2;
  const cy = alto / 2;
  const puntos: string[] = [];
  for (let i = 0; i < puntas * 2; i += 1) {
    const fuera = i % 2 === 0;
    const rx = fuera ? cx : cx * hundido;
    const ry = fuera ? cy : cy * hundido;
    const angulo = (i / (puntas * 2)) * Math.PI * 2 - Math.PI / 2;
    puntos.push(`${n(cx + rx * Math.cos(angulo))} ${n(cy + ry * Math.sin(angulo))}`);
  }
  return `M ${puntos.join(' L ')} Z`;
}

/**
 * Escudo: hombros rectos y punta abajo.
 *
 * Es la forma de un club o una pena. Los lados bajan rectos hasta dos tercios y
 * a partir de ahi curvan hacia la punta, que es como se dibuja de verdad; una
 * "U" con la punta marcada no se lee como escudo.
 */
function escudo(ancho: number, alto: number): string {
  const hombro = alto * 0.62;
  return [
    `M 0 0`,
    `L ${n(ancho)} 0`,
    `L ${n(ancho)} ${n(hombro)}`,
    `C ${n(ancho)} ${n(alto * 0.85)} ${n(ancho * 0.75)} ${n(alto)} ${n(ancho / 2)} ${n(alto)}`,
    `C ${n(ancho * 0.25)} ${n(alto)} 0 ${n(alto * 0.85)} 0 ${n(hombro)}`,
    'Z',
  ].join(' ');
}

/** Banderola: rectangulo con la cola en pico, para nombres y lemas. */
function banderola(ancho: number, alto: number): string {
  const pico = Math.min(ancho * 0.15, alto * 0.5);
  return [
    `M 0 0`,
    `L ${n(ancho)} 0`,
    `L ${n(ancho - pico)} ${n(alto / 2)}`,
    `L ${n(ancho)} ${n(alto)}`,
    `L 0 ${n(alto)}`,
    `L ${n(pico)} ${n(alto / 2)}`,
    'Z',
  ].join(' ');
}

/**
 * Corazon: dos medias elipses arriba y la punta abajo.
 *
 * Con arcos y no con curvas de Bezier a propósito. Una Bezier queda POR DENTRO
 * de sus puntos de control, asi que para que el corazon tocara los bordes habia
 * que sacar los controles fuera del rectangulo, y entonces la forma medía menos
 * de lo que ocupaba su caja: al colocarla parecia mas pequena de lo pedido y el
 * aviso de «se sale de la zona» saltaba por un hueco invisible. Un arco SI pasa
 * por sus extremos, y aqui cada uno mide media elipse exacta.
 */
function corazon(ancho: number, alto: number): string {
  const cx = ancho / 2;
  const rx = ancho / 4;
  const hombro = alto * 0.4; // altura a la que se juntan los dos lobulos
  return [
    `M 0 ${n(hombro)}`,
    `A ${n(rx)} ${n(hombro)} 0 0 1 ${n(cx)} ${n(hombro)}`,
    `A ${n(rx)} ${n(hombro)} 0 0 1 ${n(ancho)} ${n(hombro)}`,
    `Q ${n(ancho)} ${n(alto * 0.72)} ${n(cx)} ${n(alto)}`,
    `Q 0 ${n(alto * 0.72)} 0 ${n(hombro)}`,
    'Z',
  ].join(' ');
}

/** Flecha hacia la derecha, con el asta a media altura. */
function flecha(ancho: number, alto: number): string {
  const punta = ancho * 0.7;
  const grosor = alto * 0.3;
  const arriba = (alto - grosor) / 2;
  return [
    `M 0 ${n(arriba)}`,
    `L ${n(punta)} ${n(arriba)}`,
    `L ${n(punta)} 0`,
    `L ${n(ancho)} ${n(alto / 2)}`,
    `L ${n(punta)} ${n(alto)}`,
    `L ${n(punta)} ${n(arriba + grosor)}`,
    `L 0 ${n(arriba + grosor)}`,
    'Z',
  ].join(' ');
}

/** Rectangulo con las esquinas redondeadas. */
function rectanguloRedondeado(ancho: number, alto: number, radio: number): string {
  const r = Math.max(0, Math.min(radio, ancho / 2, alto / 2));
  if (r === 0) return `M 0 0 L ${n(ancho)} 0 L ${n(ancho)} ${n(alto)} L 0 ${n(alto)} Z`;
  return [
    `M ${n(r)} 0`,
    `L ${n(ancho - r)} 0`,
    `A ${n(r)} ${n(r)} 0 0 1 ${n(ancho)} ${n(r)}`,
    `L ${n(ancho)} ${n(alto - r)}`,
    `A ${n(r)} ${n(r)} 0 0 1 ${n(ancho - r)} ${n(alto)}`,
    `L ${n(r)} ${n(alto)}`,
    `A ${n(r)} ${n(r)} 0 0 1 0 ${n(alto - r)}`,
    `L 0 ${n(r)}`,
    `A ${n(r)} ${n(r)} 0 0 1 ${n(r)} 0`,
    'Z',
  ].join(' ');
}

/**
 * El trazado de una forma, en milimetros.
 *
 * Todas ocupan exactamente el rectangulo (0,0)-(ancho,alto): asi la caja que se
 * calcula al colocarla coincide con lo que se ve, y el aviso de «se sale de la
 * zona» no se dispara por un hueco que nadie ve.
 */
export function trazadoDeForma(
  forma: NombreForma,
  anchoMm: number,
  altoMm: number,
  opciones: OpcionesForma = {},
): string {
  const puntas = Math.max(3, Math.round(opciones.puntas ?? 5));
  const hundido = Math.max(0.1, Math.min(0.9, opciones.hundido ?? 0.42));

  switch (forma) {
    case 'rectangulo':
      return `M 0 0 L ${n(anchoMm)} 0 L ${n(anchoMm)} ${n(altoMm)} L 0 ${n(altoMm)} Z`;
    case 'rectangulo-redondeado':
      return rectanguloRedondeado(anchoMm, altoMm, opciones.radioMm ?? Math.min(anchoMm, altoMm) * 0.15);
    case 'circulo': {
      const rx = anchoMm / 2;
      const ry = altoMm / 2;
      return (
        `M 0 ${n(ry)} A ${n(rx)} ${n(ry)} 0 1 0 ${n(anchoMm)} ${n(ry)} ` +
        `A ${n(rx)} ${n(ry)} 0 1 0 0 ${n(ry)} Z`
      );
    }
    case 'linea':
      return `M 0 0 L ${n(anchoMm)} 0`;
    case 'triangulo':
      return `M ${n(anchoMm / 2)} 0 L ${n(anchoMm)} ${n(altoMm)} L 0 ${n(altoMm)} Z`;
    case 'estrella':
      return estrella(anchoMm, altoMm, puntas, hundido);
    case 'poligono':
      return poligono(anchoMm, altoMm, puntas);
    case 'corazon':
      return corazon(anchoMm, altoMm);
    case 'flecha':
      return flecha(anchoMm, altoMm);
    case 'escudo':
      return escudo(anchoMm, altoMm);
    case 'banderola':
      return banderola(anchoMm, altoMm);
  }
}

/** Proporcion natural de cada forma: un escudo apaisado no se lee como escudo. */
export function proporcionDeForma(forma: NombreForma): number {
  switch (forma) {
    case 'escudo':
      return 0.82;
    case 'corazon':
      return 1.05;
    case 'banderola':
      return 2.6;
    case 'flecha':
      return 2.2;
    case 'linea':
      return 8;
    case 'rectangulo':
    case 'rectangulo-redondeado':
      return 2.4;
    default:
      return 1;
  }
}

/** Las formas que solo tienen sentido con trazo y no con relleno. */
export function esSoloTrazo(forma: NombreForma): boolean {
  return forma === 'linea';
}
